/**
 * NOTE: This utility remains in core-react (rather than core) because it
 * depends on sibling utilities (get-entity-endpoint, prepare-entity-body-data)
 * that are also in core-react.
 */
import type {
  ConfigEntity,
  ConfigEntityPatchEndpointResponse,
  DomainEntity,
  EntityRootData,
  ConfigEntityRootData,
  UserInfo,
} from '@web-config-app/core';
import {
  isConfigEntity,
  operationToConfigEntityAuthAction,
  EntityStatusValue,
} from '@web-config-app/core';
import {
  API_JSON_CONTENT_TYPE,
  buildConfigEntityAuthHeaders,
} from '@web-config-app/api';
import { captureError } from '@leagueplatform/observability';
import { getEntityEndpoint } from '../get-entity-endpoint/get-entity-endpoint.util';
import { prepareEntityBodyData } from '../prepare-entity-body-data/prepare-entity-body-data';
import { assertCompletionCriteriaPublishable } from '../assert-completion-criteria-publishable/assert-completion-criteria-publishable';

export interface PublishEntityOptions {
  entity: ConfigEntity | DomainEntity;
  instanceId: string;
  data: EntityRootData | ConfigEntityRootData | undefined;
  fetchFn: (path: string, options?: RequestInit) => Promise<Response>;
  userInfo?: UserInfo;
  status?: string;
  /**
   * The domainEntityType for this entity, used in the
   * X-League-Config-Entity-Type header. When omitted, falls back
   * to the type from the entity config object.
   */
  domainEntityType?: string;
}

/**
 * Imperatively publishes or republishes a single entity by ID.
 *
 * This is a standalone async function (not a hook) that can be called
 * in a loop for bulk operations. It mirrors the logic in `useEntityUpdate`
 * but without React hook dependencies.
 */
export const publishEntity = async ({
  entity,
  instanceId,
  data,
  fetchFn,
  userInfo,
  status,
  domainEntityType: domainEntityTypeProp,
}: PublishEntityOptions): Promise<ConfigEntityPatchEndpointResponse> => {
  const operation =
    status === EntityStatusValue.Published ? 'republish' : 'publish';

  const { endpoints } = entity;
  const patchOperation = endpoints[operation];

  if (!patchOperation) {
    throw new Error(
      `Entity does not support the '${operation}' operation. Instance: ${instanceId}`,
    );
  }

  const { generateDomainEntityId } = isConfigEntity(entity)
    ? entity.domainEntity
    : entity;

  const entityInstanceIdParam = patchOperation?.pathParameters?.find(
    (param) => patchOperation.path.indexOf(`${param.name}`) > -1,
  );
  const params = [
    { name: entityInstanceIdParam?.name ?? 'id', value: instanceId },
  ];

  const { endpointFetch } =
    getEntityEndpoint<ConfigEntityPatchEndpointResponse>(
      patchOperation,
      fetchFn,
    );

  const shouldInjectDomainEntityId =
    generateDomainEntityId?.enabled && entity.type === 'config-entity';

  const domainEntityType =
    domainEntityTypeProp ??
    (isConfigEntity(entity) ? entity.domainEntity.type : entity.type);
  const action = operationToConfigEntityAuthAction[operation];

  assertCompletionCriteriaPublishable({ data, entity, domainEntityType });

  const bodyData = prepareEntityBodyData({
    data,
    entity,
    operationEndpoint: patchOperation,
    userInfo,
    shouldInjectDomainEntityId,
    generateDomainEntityId,
  });

  // Diagnostic: pair with the post-fetch capture in `execute-deep-publish` to
  // localise where `completionData.completionCriteria` is lost between the
  // fresh GET and the outgoing PATCH. Reads `bodyData` directly rather than
  // going through the guard's `getCompletionData`, so a defect in that
  // helper cannot make the two diagnostics share the same blind spot.
  if (domainEntityType === 'config-activity') {
    // `bodyData` is post-transformer output whose completion-signal fields
    // aren't declared on the root types; name the two positions we actually
    // read so a future rename surfaces as a compile break, not a silent
    // no-op the diagnostic would swallow.
    type CompletionSignal = {
      eventId?: unknown;
      completionCriteria?: unknown;
    };
    const attrs = bodyData?.attributes as
      | {
          domainEntityAttributes?: { completionData?: CompletionSignal };
          completionData?: CompletionSignal;
        }
      | undefined;
    const completionData =
      attrs?.domainEntityAttributes?.completionData ?? attrs?.completionData;
    if (
      completionData?.eventId === 'task_completed' &&
      !completionData?.completionCriteria
    ) {
      captureError(
        new Error(
          'Publish about to send a config-activity with generic completion event and no completionCriteria after prepareEntityBodyData',
        ),
        {
          errorName: 'DeepPublishMissingCompletionCriteriaDebug',
          context: {
            DeepPublishMissingCompletionCriteria: {
              stage: 'post-prepare',
              entityId: instanceId,
              domainEntityType,
              completionDataPreview: JSON.stringify(completionData)?.slice(
                0,
                500,
              ),
            },
          },
        },
      );
    }
  }

  const body = JSON.stringify({
    data: bodyData,
  });

  return endpointFetch({
    params,
    body,
    headers: {
      'Content-Type': API_JSON_CONTENT_TYPE,
      ...buildConfigEntityAuthHeaders(domainEntityType, action),
    },
  });
};
